/**
 * bugBoardService — UAT-portal gated shared status-board read client.
 *
 * Story: epic-uat-portal-001-11.
 *
 * Reads the already-merged, live, tester-session-gated oracle-bridge endpoints
 * so a logged-in tester can see a board of ALL reported bugs (summary rows) and
 * open one for its full body + screenshots. No tester ever sees another tester's
 * contact — the backend already strips reporter/email/contact from these member
 * surfaces, and this client re-asserts that at the boundary by projecting every
 * payload down to the whitelisted member-safe shape (defence in depth).
 *
 * Every call goes through `authenticatedFetch`, which injects the tester bearer
 * and owns the 401 lifecycle (clear token + redirect to /login). On a 401 this
 * still throws so the page never mistakes a dead session for an empty board.
 *
 * Errors surface as a typed `BugBoardApiError` carrying the HTTP status and the
 * backend's machine-readable `error` code, mirroring `BugSubmitApiError`, so the
 * page can branch on status/code rather than parsing strings.
 *
 * Backend contract (shipped + deployed on oracle.staging.helloworlddao.com):
 *   GET /api/uat/bugs
 *     → 200 { bugs: Array<{ id, title, status: MemberStatus }> }
 *   GET /api/uat/bugs/:id
 *     → 200 { id, title, status, fields: {label,value}[], screenshots: {name,url}[] }
 *     | 404 { error: 'not_found' }
 *   GET /api/uat/bugs/shot/:name   → raw image bytes, authed (needs the bearer)
 *
 * A screenshot's `url` is a RELATIVE proxy path (e.g. /api/uat/bugs/shot/x.png)
 * and the bytes are authed, so a screenshot cannot be a bare <img src>; callers
 * fetch it via `fetchScreenshotBlob`, then `URL.createObjectURL` the blob.
 */

import { getOracleBridgeUrl } from '@/utils/oracleBridge';
import { authenticatedFetch } from '@/auth/authenticatedFetch';

/** The six fixed member-facing bug statuses (the only labels the board renders). */
export type MemberStatus =
  | 'New'
  | 'Triaging'
  | 'In progress'
  | 'Fixed'
  | 'Closed'
  | 'Duplicate';

/** A board summary row — member-safe by construction (no reporter/contact). */
export interface MemberSafeBug {
  id: string;
  title: string;
  status: MemberStatus;
}

/** One label/value detail line of a bug (the "decided shape" fields). */
export interface BugDetailField {
  label: string;
  value: string;
}

/** One screenshot reference — `url` is a relative, authed proxy path. */
export interface BugScreenshot {
  name: string;
  url: string;
}

/** A full bug detail — member-safe by construction (no reporter/contact). */
export interface MemberSafeBugDetail {
  id: string;
  title: string;
  status: MemberStatus;
  fields: BugDetailField[];
  screenshots: BugScreenshot[];
}

/**
 * Typed error for status-board read failures. `code` is the backend's
 * machine-readable `error` field ('not_found') when present, else a generic
 * fallback.
 */
export class BugBoardApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status: number, code: string) {
    super(message);
    this.name = 'BugBoardApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Read a JSON error body WITHOUT letting a parse failure pose as data. A
 * non-JSON / empty body yields a generic code, never a thrown parse error that
 * would mask the real HTTP status (failure-as-plausible-value: the status is the
 * source of truth — HTTP is decided by 200/404, never by a field in the body).
 */
async function readErrorCode(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body?.error === 'string' ? body.error : 'unknown_error';
  } catch {
    return 'unknown_error';
  }
}

/** Coerce an unknown value to a string, treating null/undefined as empty. */
function str(value: unknown): string {
  return value == null ? '' : String(value);
}

/**
 * Project an unknown list item down to the whitelisted summary shape. Only
 * `id` / `title` / `status` survive — any extra keys on the payload (a reporter,
 * an email) are dropped here, never reaching the DOM.
 */
function projectBug(raw: unknown): MemberSafeBug {
  const rec = (raw ?? {}) as Record<string, unknown>;
  return {
    id: str(rec.id),
    title: str(rec.title),
    status: rec.status as MemberStatus,
  };
}

/**
 * List all reported bugs (summary rows) for the board.
 *
 * @returns the member-safe summary rows (projected — no contact fields)
 * @throws BugBoardApiError on any non-200 (incl. 401 — authenticatedFetch has
 *   already redirected) or a malformed body.
 */
export async function listBugs(): Promise<MemberSafeBug[]> {
  const response = await authenticatedFetch(`${getOracleBridgeUrl()}/api/uat/bugs`);

  if (!response.ok) {
    const code = await readErrorCode(response);
    const message =
      response.status === 401
        ? 'Your tester session has expired. Please sign in again.'
        : 'Could not load the status board. Please try again.';
    throw new BugBoardApiError(message, response.status, code);
  }

  const body = (await response.json().catch(() => null)) as { bugs?: unknown } | null;
  if (!body || !Array.isArray(body.bugs)) {
    throw new BugBoardApiError(
      'The status board returned an unexpected response.',
      response.status,
      'malformed_response',
    );
  }
  return body.bugs.map(projectBug);
}

/**
 * Fetch one bug's full detail by id.
 *
 * The returned object is projected to EXACTLY the member-safe fields — any
 * reporter/email/contact keys present on the raw payload are dropped at this
 * boundary and never reach the caller or the DOM.
 *
 * @param id - the member-facing bug id
 * @returns the member-safe detail (title, status, label/value fields, screenshots)
 * @throws BugBoardApiError on 404 (not_found — friendly message), any other
 *   non-200, or a malformed body.
 */
export async function getBug(id: string): Promise<MemberSafeBugDetail> {
  const response = await authenticatedFetch(
    `${getOracleBridgeUrl()}/api/uat/bugs/${encodeURIComponent(id)}`,
  );

  if (!response.ok) {
    const code = await readErrorCode(response);
    let message: string;
    if (response.status === 404) {
      message = 'That report could not be found. It may have been removed.';
    } else if (response.status === 401) {
      message = 'Your tester session has expired. Please sign in again.';
    } else {
      message = 'Could not load this report. Please try again.';
    }
    throw new BugBoardApiError(message, response.status, code);
  }

  const body = (await response.json().catch(() => null)) as
    | Record<string, unknown>
    | null;
  if (!body || typeof body.id !== 'string') {
    throw new BugBoardApiError(
      'This report returned an unexpected response.',
      response.status,
      'malformed_response',
    );
  }

  // DEFENSIVE PROJECTION — whitelist ONLY these keys. Anything else on the
  // payload (reporter, email, contact, …) is dropped here and cannot leak.
  const rawFields = Array.isArray(body.fields) ? body.fields : [];
  const rawShots = Array.isArray(body.screenshots) ? body.screenshots : [];
  return {
    id: body.id,
    title: str(body.title),
    status: body.status as MemberStatus,
    fields: rawFields.map((f) => {
      const rec = (f ?? {}) as Record<string, unknown>;
      return { label: str(rec.label), value: str(rec.value) };
    }),
    screenshots: rawShots.map((s) => {
      const rec = (s ?? {}) as Record<string, unknown>;
      return { name: str(rec.name), url: str(rec.url) };
    }),
  };
}

/**
 * Fetch a single screenshot's raw bytes through the authed proxy. The caller
 * turns the blob into an object URL (`URL.createObjectURL`) and MUST revoke it.
 *
 * @param url - the relative proxy path from a screenshot ref (e.g.
 *   /api/uat/bugs/shot/x.png); resolved against the oracle-bridge base.
 * @returns the image Blob
 * @throws BugBoardApiError on any non-200 (so a failed shot shows a placeholder,
 *   never a broken bare <img>).
 */
export async function fetchScreenshotBlob(url: string): Promise<Blob> {
  const response = await authenticatedFetch(`${getOracleBridgeUrl()}${url}`);
  if (!response.ok) {
    throw new BugBoardApiError(
      'Could not load a screenshot.',
      response.status,
      'screenshot_failed',
    );
  }
  return response.blob();
}
